import { useSyncExternalStore } from "react";

import { prefersReducedMotion, startMarkedTransition } from "./motion";

/**
 * Light or dark, chosen by each person in their browser (decision Denis
 * 24.10.2026, design 2026-10-24-stahl-oberflaeche §5.1).
 *
 * Light is the default: the CI is light, dark is an offer. The choice lives
 * on `<html data-theme>`, where tokens.css reads it — the inline script in
 * index.html sets it before the first paint from the same key, so a page
 * stored as dark never flashes light. Storage is a convenience: blocked (a
 * private window) or holding something else, it reads as light, and a switch
 * still holds for the session because the attribute, not the storage, is
 * what the page and this module read.
 *
 * The switch reveals the new mode as a circle opening from the switch (a
 * view transition, `--dur-5`, base.css). Without view transitions (Firefox
 * before 144, jsdom) or under reduced motion it changes at once.
 */
export type Theme = "light" | "dark";

/** The storage key — index.html reads the same one before React starts. */
export const THEME_KEY = "k60.theme";

/** The stored choice: `"dark"` only when exactly that is stored. */
export function storedTheme(): Theme {
  try {
    return window.localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Every `useTheme` on screen, so each switch shows the same choice. */
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** What the page shows — the attribute, not a copy of it. */
function shownTheme(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

/** Shows `theme` on the page and tells every switch. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  for (const listener of listeners) listener();
}

/**
 * Stores and shows `next`. Where a view transition may run, the new mode
 * opens as a circle from `origin` (the middle of the switch, in viewport
 * px; the middle of the window without one): the origin and `data-vt` go on
 * `<html>` before the browser takes its picture of the old page, and
 * `data-vt` goes again once the reveal is over — left standing, the next page
 * turn would open as a circle too (`startMarkedTransition`, lib/motion.ts).
 * Under `data-vt` no element keeps a transition name of its own (base.css):
 * the masthead, the marks and the page are all in the one picture the circle
 * opens over, instead of cross-fading beside it.
 */
export function switchTheme(next: Theme, origin?: { x: number; y: number }): void {
  try {
    window.localStorage.setItem(THEME_KEY, next);
  } catch {
    // Blocked storage: the choice holds for this session only.
  }

  if (typeof document.startViewTransition !== "function" || prefersReducedMotion()) {
    applyTheme(next);
    return;
  }

  const root = document.documentElement;
  const { x, y } = origin ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  root.style.setProperty("--enthuellung-x", `${x}px`);
  root.style.setProperty("--enthuellung-y", `${y}px`);
  // The switch's own state is drawn inside the new picture, not a frame after
  // it. A second switch inside the reveal skips this one, and the mode is
  // applied all the same — no failure.
  startMarkedTransition("theme", () => applyTheme(next));
}

/** The mode on screen and the way to change it — the same in every switch. */
export function useTheme(): {
  theme: Theme;
  setTheme: (next: Theme, origin?: { x: number; y: number }) => void;
} {
  const theme = useSyncExternalStore(subscribe, shownTheme, () => "light" as const);
  return { theme, setTheme: switchTheme };
}
